import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useDiagramStore, useHistoryStore, useViewportStore } from '@/stores'
import { useTabViewStore } from '@/stores/tabViewStore'
import { getTableWidth, MIN_TABLE_WIDTH } from '@/utils/geometry'

export type ResizeDirection = 'e' | 'w'

interface ResizeState {
  pointerId: number
  tableId: string
  direction: ResizeDirection
  startClientX: number
  x: number
  width: number
}

export function useTableResize(tableId: string) {
  const resizeRef = useRef<ResizeState | null>(null)
  const movedRef = useRef(false)
  const restoreCursorRef = useRef('')
  const restoreUserSelectRef = useRef('')
  const [isResizing, setIsResizing] = useState(false)

  const finishResize = useCallback((cancelled: boolean) => {
    const resize = resizeRef.current
    if (!resize) return

    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', handlePointerUp)
    window.removeEventListener('pointercancel', handlePointerCancel)

    if (cancelled) {
      useDiagramStore.setState((state) => ({
        tables: state.tables.map((table) => table.id === resize.tableId
          ? { ...table, x: resize.x, width: resize.width }
          : table),
      }))
    } else if (movedRef.current) {
      const { tables, rels, groups, defaults, projectName, dialect } = useDiagramStore.getState()
      useHistoryStore.getState().commitHistory(
        {
          tables,
          rels,
          groups,
          defaults,
          projectName,
          dialect,
          hiddenTableIds: useTabViewStore.getState().hiddenTableIds,
        },
        `resize-table:${resize.tableId}`,
      )
    }

    document.body.style.cursor = restoreCursorRef.current
    document.body.style.userSelect = restoreUserSelectRef.current
    resizeRef.current = null
    movedRef.current = false
    setIsResizing(false)
  // Os listeners sao callbacks estaveis registrados uma vez por gesto, como no drag de tabela.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const resize = resizeRef.current
    if (!resize || event.pointerId !== resize.pointerId) return
    event.preventDefault()

    const scale = useViewportStore.getState().sc
    if (!Number.isFinite(scale) || scale <= 0) return

    const dx = (event.clientX - resize.startClientX) / scale
    const movesWest = resize.direction === 'w'
    const movesEast = resize.direction === 'e'

    let width = resize.width
    let x = resize.x

    if (movesEast) width = Math.max(MIN_TABLE_WIDTH, resize.width + dx)
    if (movesWest) {
      width = Math.max(MIN_TABLE_WIDTH, resize.width - dx)
      x = resize.x + resize.width - width
    }

    movedRef.current = movedRef.current || Math.abs(dx) > 0.5
    setIsResizing(movedRef.current)
    useDiagramStore.setState((state) => ({
      tables: state.tables.map((table) => table.id === resize.tableId
        ? { ...table, x, width }
        : table),
    }))
  }, [])

  const handlePointerUp = useCallback((event: PointerEvent) => {
    if (resizeRef.current?.pointerId !== event.pointerId) return
    finishResize(false)
  }, [finishResize])

  const handlePointerCancel = useCallback((event: PointerEvent) => {
    if (resizeRef.current?.pointerId !== event.pointerId) return
    finishResize(true)
  }, [finishResize])

  useEffect(() => () => finishResize(true), [finishResize])

  const startResize = useCallback((
    event: ReactPointerEvent<HTMLElement>,
    direction: ResizeDirection,
  ) => {
    if (event.button !== 0) return
    const table = useDiagramStore.getState().tables.find((entry) => entry.id === tableId)
    if (!table) return

    event.preventDefault()
    event.stopPropagation()

    const cursor = `${direction}-resize`
    restoreCursorRef.current = document.body.style.cursor
    restoreUserSelectRef.current = document.body.style.userSelect
    document.body.style.cursor = cursor
    document.body.style.userSelect = 'none'

    resizeRef.current = {
      pointerId: event.pointerId,
      tableId,
      direction,
      startClientX: event.clientX,
      x: table.x,
      width: getTableWidth(table),
    }
    movedRef.current = false

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerCancel)
  }, [handlePointerCancel, handlePointerMove, handlePointerUp, tableId])

  return { isResizing, startResize }
}
